import {FC} from 'react'
import {Settings} from 'react-slick'

import {TestimonialQuote, useTestimonialsContent} from '@/content/marketing-pages/testimonials'
import {useTranslation} from '@/i18n'
import {SLIDER_TRANSITION} from '@/theme/cssTransitions'
import {theme} from '@/theme/theme'
import {MOBILE_MAX_WIDTH, MOBILE_SCREENSIZE, TABLET_SCREENSIZE} from '@/utils/styles/breakpoints'

import Carousel from '../common/carousel/Carousel'
import {Section} from '../common/content/Section'
import {Text, TextVariant} from '../common/content/Text'
import {Quote} from './Quote'

const settings: Settings = {
  infinite: true,
  speed: SLIDER_TRANSITION,
  slidesToShow: 1,
  slidesToScroll: 1,
  autoplay: true,
  autoplaySpeed: 8000,
  arrows: true,
  responsive: [
    {
      breakpoint: MOBILE_MAX_WIDTH,
      settings: {
        dots: true,
        arrows: false,
      },
    },
  ],
}

type TestimonialProps = {
  title?: string
  quotes?: TestimonialQuote[]
}

export const Testimonial: FC<TestimonialProps> = ({title, quotes}) => {
  const {t} = useTranslation()
  const defaultContent = useTestimonialsContent()
  const data = quotes || defaultContent.list

  return (
    <>
      <Section hasPaddingTop id="testimonial">
        <Text variant={TextVariant.h2} align="center">
          {title || t(defaultContent.title)}
        </Text>
        <div className="testimonial">
          {data.length === 1 ? (
            <Quote
              key={data[0].src}
              photoSrc={data[0].src}
              photoAlt={data[0].alt}
              quote={t(data[0].quote)}
              author={t(data[0].author)}
              role={t(data[0].role)}
            />
          ) : (
            <Carousel settings={settings}>
              {data.map((item) => (
                <Quote
                  key={item.src}
                  photoSrc={item.src}
                  photoAlt={item.alt}
                  quote={t(item.quote)}
                  author={t(item.author)}
                  role={t(item.role)}
                />
              ))}
            </Carousel>
          )}
        </div>
      </Section>

      <style jsx>{`
        .testimonial {
          margin-top: ${theme.spacing[8]};
        }
        @media ${MOBILE_SCREENSIZE} {
          .testimonial {
            margin-top: ${theme.spacing[6]};
          }
        }
      `}</style>

      <style jsx global>{`
        .testimonial .slick-slider {
          max-width: 100%;
        }
        .testimonial .slick-list {
          margin: 0 -${theme.spacing[3]};
        }
        .testimonial .slick-slide {
          padding: 0 ${theme.spacing[3]};
        }
        .testimonial .slick-prev,
        .testimonial .slick-next {
          top: inherit;
          bottom: 8px;
          z-index: 1;
        }
        .testimonial .slick-prev {
          left: inherit;
          right: 40px;
        }
        .testimonial .slick-next {
          right: 0;
        }
        @media ${TABLET_SCREENSIZE} {
          .testimonial .slick-slider {
            max-width: 520px;
          }
          .testimonial .slick-list {
            margin: 0 -${theme.spacing[2]};
          }
          .testimonial .slick-slide {
            padding: 0 ${theme.spacing[2]};
          }
          .testimonial .slick-prev,
          .testimonial .slick-next {
            top: 50%;
          }
          .testimonial .slick-prev {
            left: -56px;
          }
          .testimonial .slick-next {
            right: -56px;
          }
        }
      `}</style>
    </>
  )
}
